import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker, TimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { ScheduleNotifyInput, ScheduleOnEndInput } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import {
  emptyCondition,
  RulesBuilder,
  useRuleFields,
  type RuleValueKind,
} from '@/features/product-rules';
import { TimezoneSelect } from '@/features/stores';
import { BASE_CURRENCY } from '@/shared/catalog/base-currency';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { mapScheduleField } from '../api/mappers';
import { scheduleErrorCopy, type ScheduleAction } from '../api/schedule-errors';
import {
  MAX_CYCLES,
  MAX_INTERVAL_DAYS,
  MAX_LIVE_COUNT,
  MAX_SCHEDULE_STORES,
  scheduleFormSchema,
  type ScheduleFormInput,
  type ScheduleFormOutput,
} from '../schemas/schedule-form-schema';

import { ScheduleErrorAlert } from './schedule-error-alert';
import { ScheduleStoresField, type StoreOption } from './schedule-stores-field';

export const SCHEDULE_FORM_ID = 'schedule-form';

export type SubmitIntent = 'save' | 'preview';

/** Fields the backend may name that the form shows; anything else lands in the form's alert. */
const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'stores',
  'liveCount',
  'intervalDays',
  'runAt',
  'timeZone',
  'startsAt',
  'endsAt',
  'maxCycles',
  'loop',
  'onEnd',
  'notify',
  'rules',
  'products',
  ...Array.from({ length: MAX_SCHEDULE_STORES }, (_, i) => [
    `stores.${i}.storeId`,
    `stores.${i}.redirectCollectionId`,
  ]).flat(),
  ...Array.from({ length: 20 }, (_, i) => [
    `rules.conditions.${i}.field`,
    `rules.conditions.${i}.operator`,
    `rules.conditions.${i}.value`,
  ]).flat(),
]);

export interface ScheduleFormProps {
  mode: 'create' | 'edit';
  defaultValues: ScheduleFormInput;
  stores: readonly StoreOption[];
  isSubmitting: boolean;
  /** Read-only: no `products.write`, or the schedule has ended. */
  disabled?: boolean;
  /**
   * Saves the values. Answer `false` when nothing was saved (a confirmation was declined): the
   * form then stays dirty on the save bar.
   */
  onSubmit: (
    values: ScheduleFormOutput,
    kindOf: (field: string) => RuleValueKind,
    intent: SubmitIntent,
  ) => Promise<unknown>;
  /** Main-column sections after the cadence: the pool picker on the create page. */
  children?: ReactNode;
  /** Side-rail cards under the save card. */
  aside?: ReactNode;
}

/**
 * The rotation's settings: name, target stores, cadence on the schedule's own clock, how it ends,
 * who hears about runs, and an optional rule set that adds matching master products to the pool.
 * The create page saves a draft (optionally straight into the preview); the Settings tab saves
 * through the save bar.
 */
export function ScheduleForm({
  mode,
  defaultValues,
  stores,
  isSubmitting,
  disabled = false,
  onSubmit,
  children,
  aside,
}: ScheduleFormProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const form = useForm<ScheduleFormInput, unknown, ScheduleFormOutput>({
    resolver: useI18nResolver(scheduleFormSchema),
    defaultValues,
    disabled,
  });
  const { fields, kindOf } = useRuleFields();
  const [serverError, setServerError] = useState<unknown>(null);
  const action: ScheduleAction = mode === 'create' ? 'create' : 'update';
  const [targets, timeZone, rulesEnabled, loop, products] = useWatch({
    control: form.control,
    name: ['stores', 'timeZone', 'rulesEnabled', 'loop', 'products'],
  });
  const titles = useMemo(
    () => new Map(products.flatMap((p) => (p.title ? [[p.id, p.title] as const] : []))),
    [products],
  );

  // The Settings tab hands in fresh defaults whenever the schedule refetches (a run, a pause,
  // polling every few seconds). Re-seed in place instead of remounting: untouched fields take
  // the server's values, fields being edited keep what was typed, and an unchanged schedule
  // (only `updatedAt` or the run claim moved) is not reset at all.
  const seeded = useRef(JSON.stringify(defaultValues));
  useEffect(() => {
    const next = JSON.stringify(defaultValues);
    if (next === seeded.current) return;
    seeded.current = next;
    form.reset(defaultValues, { keepDirtyValues: true });
  }, [defaultValues, form]);
  // Start and end are wall-clock values: a new zone keeps the clock time, so the instants move.
  const zoneMoved = Boolean(timeZone) && timeZone !== defaultValues.timeZone;

  // On a new schedule the zone follows the first ticked store until someone picks one.
  const [zoneTouched, setZoneTouched] = useState(mode === 'edit');
  const firstStoreZone = stores.find((s) => s.id === targets[0]?.storeId)?.timezone;
  useEffect(() => {
    if (zoneTouched || !firstStoreZone) return;
    if (form.getValues('timeZone') !== firstStoreZone) {
      form.setValue('timeZone', firstStoreZone, { shouldDirty: true });
    }
  }, [firstStoreZone, form, zoneTouched]);

  const submitAs = (intent: SubmitIntent) =>
    form.handleSubmit(async (values) => {
      form.clearErrors('root.server');
      setServerError(null);
      try {
        const saved = await onSubmit(values, kindOf, intent);
        if (mode === 'edit' && saved !== false) form.reset(values);
      } catch (error) {
        if (scheduleErrorCopy(error, action)) {
          setServerError(error);
          return;
        }
        if (
          !applyFieldErrors(error, form.setError, {
            knownFields: KNOWN_FIELDS,
            mapField: mapScheduleField,
          })
        ) {
          setServerError(error);
        }
      }
    });
  const submit = submitAs('save');
  useSaveBar({ form, onSave: submit, saving: isSubmitting, disabled });

  const rootError = form.formState.errors.root?.server?.message;
  const zoneHint = t('schedules:fields.inZone', { zone: timeZone || '—' });

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection
              title={t('schedules:sections.ending')}
              description={t('schedules:sections.endingDescription')}
              tour="schedules.form.ending"
            >
              <FormField
                control={form.control}
                name="onEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.onEnd')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                        className="gap-2.5"
                      >
                        {Object.values(ScheduleOnEndInput).map((value) => (
                          <div key={value} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={value}
                              id={`schedule-on-end-${value}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`schedule-on-end-${value}`}>
                                {t(`schedules:onEnd.${value}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`schedules:onEndHint.${value}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="notify"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.notify')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={field.disabled}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(ScheduleNotifyInput).map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`schedules:notify.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`schedules:notifyHint.${field.value}`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
            {mode === 'create' && !disabled ? (
              <FormSection title={t('schedules:sections.save')} tour="schedules.form.save">
                <p className="text-muted-foreground text-sm">{t('schedules:create.saveHint')}</p>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => void submitAs('preview')()}
                  >
                    {t('schedules:create.saveAndPreview')}
                  </Button>
                  <Button
                    type="submit"
                    form={SCHEDULE_FORM_ID}
                    variant="outline"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('common:actions.saving') : t('schedules:create.saveDraft')}
                  </Button>
                </div>
              </FormSection>
            ) : null}
            {aside}
          </>
        }
      >
        <form id={SCHEDULE_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {serverError ? (
            <ScheduleErrorAlert error={serverError} action={action} titles={titles} />
          ) : null}
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection title={t('schedules:sections.details')} tour="schedules.form.details">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('schedules:fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoFocus={mode === 'create'} maxLength={120} {...field} />
                  </FormControl>
                  <FormDescription>{t('schedules:fields.nameHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('schedules:sections.stores')}
            description={t('schedules:sections.storesDescription')}
            tour="schedules.form.stores"
          >
            <FormField
              control={form.control}
              name="stores"
              render={() => (
                <FormItem>
                  <ScheduleStoresField stores={stores} disabled={disabled} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-muted-foreground text-xs">{t('schedules:explain.drafts')}</p>
          </FormSection>

          <FormSection
            title={t('schedules:sections.cadence')}
            description={t('schedules:sections.cadenceDescription')}
            tour="schedules.form.cadence"
          >
            <div className="grid gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="liveCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.liveCount')}</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" {...field} />
                    </FormControl>
                    <FormDescription>
                      {t('schedules:fields.liveCountHint', { max: MAX_LIVE_COUNT })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="intervalDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.intervalDays')}</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" {...field} />
                    </FormControl>
                    <FormDescription>
                      {t('schedules:fields.intervalDaysHint', { max: MAX_INTERVAL_DAYS })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="runAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.runAt')}</FormLabel>
                    <FormControl>
                      <TimePicker step={60} {...field} />
                    </FormControl>
                    <FormDescription>{zoneHint}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="timeZone"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('schedules:fields.timeZone')}</FormLabel>
                  <FormControl>
                    <TimezoneSelect
                      value={field.value}
                      onChange={(value) => {
                        setZoneTouched(true);
                        field.onChange(value);
                      }}
                      onBlur={field.onBlur}
                      disabled={field.disabled}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormDescription>
                    {mode === 'create' && !zoneTouched
                      ? t('schedules:fields.timeZoneDefault')
                      : t('schedules:fields.timeZoneHint')}
                  </FormDescription>
                  {zoneMoved ? (
                    <p className="text-sm" role="status">
                      {t('schedules:fields.timeZoneMoved')}
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.startsAt')}</FormLabel>
                    <FormControl>
                      <DateTimePicker timeZone={timeZone || undefined} {...field} />
                    </FormControl>
                    <FormDescription>
                      {t('schedules:fields.startsAtHint', { zone: timeZone || '—' })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.endsAt')}</FormLabel>
                    <FormControl>
                      <DateTimePicker timeZone={timeZone || undefined} {...field} />
                    </FormControl>
                    <FormDescription>{t('schedules:fields.endsAtHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="maxCycles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('schedules:fields.maxCycles')}</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        placeholder={t('schedules:fields.noLimit')}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('schedules:fields.maxCyclesHint', { max: MAX_CYCLES })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="loop"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-3 pt-6">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={field.disabled}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">{t('schedules:fields.loop')}</FormLabel>
                    </div>
                    <FormDescription>
                      {loop ? t('schedules:fields.loopOn') : t('schedules:fields.loopOff')}
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
            <p className="text-muted-foreground text-xs">{t('schedules:explain.ending')}</p>
          </FormSection>

          <FormSection
            title={t('schedules:sections.rules')}
            description={t('schedules:sections.rulesDescription')}
            tour="schedules.form.rules"
          >
            <FormField
              control={form.control}
              name="rulesEnabled"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          // A switched-off rule set is not sent, so its rows must not block saving.
                          form.setValue('rules.conditions', checked ? [emptyCondition()] : [], {
                            shouldDirty: true,
                          });
                        }}
                        disabled={field.disabled}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t('schedules:fields.rulesEnabled')}
                    </FormLabel>
                  </div>
                  <FormDescription>{t('schedules:fields.rulesHint')}</FormDescription>
                </FormItem>
              )}
            />
          </FormSection>
          {rulesEnabled ? (
            <RulesBuilder
              fields={fields}
              kindOf={kindOf}
              currency={BASE_CURRENCY}
              title={t('schedules:sections.conditions')}
              description={t('schedules:sections.conditionsDescription', {
                currency: BASE_CURRENCY,
                zone: timeZone || '—',
              })}
              tour="schedules.form.conditions"
            />
          ) : null}
        </form>
        {children}
      </FormLayout>
    </Form>
  );
}
